import { useCallback, useEffect } from 'react';
import { coerceLocale, useLocale, type Locale } from '@fetmode/i18n';

import { useAuth } from '../../features/auth/index.js';

/**
 * Survives AppShell remounts. A useRef inside the hook resets when the shell
 * remounts, which would re-apply a stale account locale and snap the UI back
 * to English mid-switch.
 */
let appliedAccountLocaleUserId: string | null = null;

/**
 * Applies account locale on login and returns a handler that persists
 * language changes to the API when signed in.
 */
export function useLocaleAccountSync(): {
  persistLocale: (locale: Locale) => void;
} {
  const { user, api, setUser } = useAuth();
  const { setLocale } = useLocale();

  useEffect(() => {
    if (!user) {
      appliedAccountLocaleUserId = null;
      return;
    }
    if (appliedAccountLocaleUserId === user.id) {
      return;
    }
    appliedAccountLocaleUserId = user.id;
    const accountLocale = coerceLocale(user.locale);
    setLocale(accountLocale);
  }, [user, setLocale]);

  const persistLocale = useCallback(
    (next: Locale) => {
      if (!user) {
        return;
      }
      // User chose explicitly — don't let a pending login-sync effect overwrite.
      appliedAccountLocaleUserId = user.id;
      void (async () => {
        try {
          await api.updateMyProfile({ locale: next });
          const updatedUser = await api.getMe();
          setUser(updatedUser);
        } catch {
          // local preference still applies
        }
      })();
    },
    [api, setUser, user],
  );

  return { persistLocale };
}
